import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthToken } from '@/utils/auth';

const CollaborationContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SOCKET_PATH = '/api/socket.io';

export function CollaborationProvider({ projectId, children }) {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [members, setMembers] = useState([]);
  const [cursors, setCursors] = useState({});
  const [audioMembers, setAudioMembers] = useState([]);
  const [viewportStates, setViewportStates] = useState({});
  const [followingUser, setFollowingUser] = useState(null);
  const listenersRef = useRef({});
  const viewportThrottleRef = useRef(null);

  const roomId = projectId ? `project_${projectId}` : null;

  useEffect(() => {
    if (!roomId || !user) return;

    // The handshake is authenticated server-side; send the session token so the
    // backend can resolve identity and verify project membership before joining.
    const socket = io(BACKEND_URL, {
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      withCredentials: true,
      auth: { token: getAuthToken() },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join_room', {
        room_id: roomId,
        user: { user_id: user.user_id, name: user.name, email: user.email, picture: user.picture }
      });
    });

    socket.on('disconnect', () => {
      setConnected(false);
      setMembers([]);
    });

    socket.on('presence_update', (data) => {
      setMembers(data.members || []);
    });

    socket.on('cursor_update', (data) => {
      setCursors(prev => ({ ...prev, [data.user_id]: data.cursor }));
    });

    socket.on('audio_user_joined', (data) => {
      setAudioMembers(prev => {
        if (prev.find(m => m.user_id === data.user_id)) return prev;
        return [...prev, { user_id: data.user_id, sid: data.sid, name: data.name }];
      });
    });

    socket.on('audio_user_left', (data) => {
      setAudioMembers(prev => prev.filter(m => m.user_id !== data.user_id));
    });

    socket.on('viewport_state_update', (data) => {
      setViewportStates(prev => ({ ...prev, [data.user_id]: data.viewport }));
    });

    return () => {
      socket.emit('leave_room', { room_id: roomId });
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
      setMembers([]);
      setCursors({});
      setAudioMembers([]);
    };
  }, [roomId, user]);

  const emitCursor = useCallback((cursor) => {
    if (socketRef.current?.connected && roomId) {
      socketRef.current.emit('cursor_move', {
        room_id: roomId,
        user_id: user?.user_id,
        cursor
      });
    }
  }, [roomId, user]);

  const emitFrameUpdate = useCallback((type, frameId, frameData) => {
    if (socketRef.current?.connected && roomId) {
      socketRef.current.emit('frame_update', {
        room_id: roomId,
        type,
        frame_id: frameId,
        frame_data: frameData,
        user_id: user?.user_id,
        user_name: user?.name
      });
    }
  }, [roomId, user]);

  const emitShotVersion = useCallback((shotId, version) => {
    if (socketRef.current?.connected && roomId) {
      socketRef.current.emit('shot_version_created', {
        room_id: roomId,
        shot_id: shotId,
        version,
        user_name: user?.name
      });
    }
  }, [roomId, user]);

  // --- Concept collaboration events ---

  const emitConceptCreated = useCallback((anchorId, concept) => {
    if (socketRef.current?.connected && roomId) {
      socketRef.current.emit('concept_created', {
        room_id: roomId,
        anchor_id: anchorId,
        concept,
        user_name: user?.name
      });
    }
  }, [roomId, user]);

  const emitConceptSelected = useCallback((anchorId, conceptId) => {
    if (socketRef.current?.connected && roomId) {
      socketRef.current.emit('concept_selected', {
        room_id: roomId,
        anchor_id: anchorId,
        concept_id: conceptId,
        user_id: user?.user_id,
        user_name: user?.name
      });
    }
  }, [roomId, user]);

  const emitConceptUpdated = useCallback((conceptId, anchorId) => {
    if (socketRef.current?.connected && roomId) {
      socketRef.current.emit('concept_updated', {
        room_id: roomId,
        concept_id: conceptId,
        anchor_id: anchorId,
        user_id: user?.user_id,
        user_name: user?.name
      });
    }
  }, [roomId, user]);

  const onEvent = useCallback((event, handler) => {
    if (socketRef.current) {
      socketRef.current.on(event, handler);
      return () => socketRef.current?.off(event, handler);
    }
  }, []);

  const joinAudio = useCallback(() => {
    if (socketRef.current?.connected && roomId) {
      socketRef.current.emit('audio_join', {
        room_id: roomId,
        user_id: user?.user_id,
        name: user?.name
      });
    }
  }, [roomId, user]);

  const leaveAudio = useCallback(() => {
    if (socketRef.current?.connected && roomId) {
      socketRef.current.emit('audio_leave', {
        room_id: roomId,
        user_id: user?.user_id
      });
    }
  }, [roomId, user]);

  const emitViewportState = useCallback((viewport) => {
    // Don't broadcast while following someone (prevents feedback loops)
    if (followingUser) return;
    // Throttle to 100ms
    if (viewportThrottleRef.current) return;
    viewportThrottleRef.current = setTimeout(() => { viewportThrottleRef.current = null; }, 100);
    if (socketRef.current?.connected && roomId) {
      socketRef.current.emit('viewport_state', {
        room_id: roomId,
        user_id: user?.user_id,
        viewport
      });
    }
  }, [roomId, user, followingUser]);

  // Auto-unfollow if followed user disconnects
  useEffect(() => {
    if (followingUser && !members.find(m => m.user_id === followingUser)) {
      setFollowingUser(null);
    }
  }, [members, followingUser]);

  // Get followed user's viewport
  const followedViewport = followingUser ? viewportStates[followingUser] : null;

  const sendAudioSignal = useCallback((targetSid, signal, type) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('audio_signal', {
        target_sid: targetSid,
        signal,
        type
      });
    }
  }, []);

  const value = {
    connected,
    members,
    cursors,
    audioMembers,
    roomId,
    emitCursor,
    emitFrameUpdate,
    emitShotVersion,
    emitConceptCreated,
    emitConceptSelected,
    emitConceptUpdated,
    emitViewportState,
    viewportStates,
    followingUser,
    setFollowingUser,
    followedViewport,
    onEvent,
    joinAudio,
    leaveAudio,
    sendAudioSignal,
    socket: socketRef
  };

  return (
    <CollaborationContext.Provider value={value}>
      {children}
    </CollaborationContext.Provider>
  );
}

export function useCollaboration() {
  const ctx = useContext(CollaborationContext);
  if (!ctx) return { connected: false, members: [], cursors: {}, audioMembers: [] };
  return ctx;
}
